import { createGlobalState, createInjectionState } from '@vueuse/core';
import type { ComputedRef } from 'vue';
import { computed, ref, unref } from 'vue';

import { ACC_FORM_MODE, purl, WEBAPP_TYPE } from '#public/commons-global';
import { useDisplayWidths } from '#public_ts/composables';

import type { MenuForm, MenuPanel } from './globals';
import { __MAIN_MENU_LAYOUT_MODE__, __MENU_PANELS__, __MENU_PANELS_COMPACT_MODE__, __SUPRESS_ACTIVE_MENU__ } from './globals';

const pageUrl = createGlobalState(() => {
    const url = purl();
    const _pageUrl = url.attr('path');
    return _pageUrl;
});

export function formUrl(path: string) {
    if(path.startsWith('http')) return path;
    if(path.startsWith('/')) return `${__ROOT__}${path.substring(1)}`;
    return `${__ROOT__}Enrollment/${path}`;
}

export function isActive(href: string) {
    if(__SUPRESS_ACTIVE_MENU__ === 'true') return false;
    return (pageUrl() == href);
}

export const DummyUserPanel: MenuPanel = {
    name: 'Utente',
    id: 'menu-panel-Utente',
    panel: [],
};

type MainMenuLayoutMode = 'auto' | 'mobile' | 'compatible';

function normalizeLayoutMode(mode: string | undefined): MainMenuLayoutMode {
    if(mode === 'auto') return 'auto';
    if(mode === 'mobile') return 'mobile';
    return 'compatible';
}

const useMainMenuLayoutMode = createGlobalState(() => {
    // must be set to auto for all forms, which use upper option menu
    // because otherwise mobile version can be shown without menu
    const layoutMode = ref<MainMenuLayoutMode>(normalizeLayoutMode(__MAIN_MENU_LAYOUT_MODE__));

    function setLayoutMode(mode: string) {
        const nextMode = normalizeLayoutMode(mode);
        layoutMode.value = nextMode;
    }

    return {
        layoutMode,
        setLayoutMode,
    };
});

export function useLayoutManagement() {
    const DISPLAY_WIDTHS = useDisplayWidths();
    const { layoutMode } = useMainMenuLayoutMode();
    const showCompactInterface = computed(() => {
        const mode = unref(layoutMode);
        if(mode === 'mobile') return true;
        // force mobile mode for phones/tablets
        if(DISPLAY_WIDTHS.isMobileView && (mode === 'auto')) return true;
        // otherwise backward compatible mode. show compact interface only for < 1200
        // full inteface is too ugly for < 1200, and for mobile it must be used for mobile menu
        return DISPLAY_WIDTHS.user < DISPLAY_WIDTHS.xl;
    });

    return {
        showCompactInterface,
    };
}

const [useProvideGlobalSearch, _useGlobalSeacrh] = createInjectionState((showCompactInterface: ComputedRef<boolean>) => {
    const toggleMenuPanelsGlobal = ref(false);
    const searchText = ref('');

    const haveFattapp2Layout = computed(() => {
        if(!showCompactInterface.value) return false;
        return __CUSTOM_WEB_APPLICATION__ === WEBAPP_TYPE.FATTAPP_V2;
    });

    // see xflist.json
    const _fattapp2AllowedForms = new Set([
        'accountInfo',
        'manageFactures',
        'fatturaSuperFacile',
        'fatturaStazioneServizioSuperFacile',
        'users',
        'products',
    ]);

    function filterFattapp2Panels() {
        // workaround - fattapp role has separate invoice list forms
        // but for fattapp2 is better merged one
        // we don't want to change roles, so we use hack here
        let haveInvoiceList = false;
        const panels = __MENU_PANELS__.map((panel) => {
            let haveAccountInfo: MenuForm | undefined;
            const items = panel.panel.map((item) => {
                if((item.id === 'manageFacturesActive')
                    || (item.id === 'manageFacturesPassive')
                    || (item.id === 'manageFactures')) {
                    if(haveInvoiceList) return undefined;
                    haveInvoiceList = true;
                    return {
                        ...item,
                        id: 'manageFactures',
                        label: 'Lista Fatture',
                        icon: 'icons/manageFactures.png',
                        path: 'manageInvoices.shtml',
                    };
                }
                if(item.id === 'accountInfo') {
                    haveAccountInfo = item;
                    return undefined;
                }
                if(_fattapp2AllowedForms.has(item.id)) return item;
                return undefined;
            }).filter((item) => item) as MenuForm[];

            if(haveAccountInfo) {
                // split account info to three forms
                items.push({
                    ...haveAccountInfo,
                    id: 'anagraficaPanel',
                    label: 'I miei dati',
                    path: `${haveAccountInfo.path}?accFormMode=${ACC_FORM_MODE.anagrafica}`,
                }, {
                    ...haveAccountInfo,
                    id: 'passwordPanel',
                    label: 'Gestione password',
                    path: `${haveAccountInfo.path}?accFormMode=${ACC_FORM_MODE.password}`,
                    icon: 'icons/accountPasswordConfig.png',
                }, {
                    ...haveAccountInfo,
                    id: 'exDataPanel',
                    label: 'Configurazione servizi',
                    path: `${haveAccountInfo.path}?accFormMode=${ACC_FORM_MODE.services}`,
                    icon: 'icons/accountServiceConfig.png',
                });
            }
            return {
                ...panel,
                panel: items,
            };
        }).filter((panel) => panel.panel.length > 0);

        let userPanel = panels.find((panel) => panel.name === 'Utente');
        if(!userPanel) {
            userPanel = { ...DummyUserPanel, panel: [] };
        }
        userPanel.panel.push({
            id: 'quickGuide',
            label: 'Guida rapida',
            icon: 'icons/quick%20guide.png',
            path: 'quickGuide.shtml',
            panel: 'Utente',
        });
        userPanel.panel.push({
            id: 'fattapp2News',
            label: 'Notizie',
            icon: 'icons/news.png',
            path: 'fattapp2News.shtml',
            panel: 'Utente',
        });
        return panels;
    }

    function filterPanels() {
        const text = unref(searchText).toLowerCase().trim();
        if(!text) return __MENU_PANELS__;
        return __MENU_PANELS__.map((panel) => {
            // backward compatible
            if(__MENU_PANELS_COMPACT_MODE__ && (panel.panel.length == 1)) return { ...panel };
            if(panel.name == 'Utente') return { ...panel };
            const items = panel.panel.filter((item) => item.label.toLowerCase().includes(text));
            return {
                ...panel,
                panel: items,
            };
        }).filter((panel) => panel.panel.length > 0);
    }

    const allPanels = computed(() => {
        switch(__CUSTOM_WEB_APPLICATION__) {
            case WEBAPP_TYPE.FATTAPP_V2:
                return filterFattapp2Panels();
            default:
                return __MENU_PANELS__;
        }
    });

    const filteredPanels = computed(() => {
        switch(__CUSTOM_WEB_APPLICATION__) {
            case WEBAPP_TYPE.FATTAPP_V2:
                return unref(allPanels);
            default:
                return filterPanels();
        }
    });

    const flattenPanels = computed(() => {
        const panels = unref(allPanels);
        const result = new Map<string, MenuForm>();
        for(const panel of panels) {
            for(const item of panel.panel) {
                result.set(item.id, item);
            }
        }
        return result;
    });

    const userPanel = computed(() => unref(filteredPanels).find((panel) => panel.name == 'Utente'));

    return {
        toggleMenuPanelsGlobal,
        searchText,
        filteredPanels,
        flattenPanels,
        userPanel,
        haveFattapp2Layout,
    };
});

export { useProvideGlobalSearch };
export function useGlobalSeacrh() {
    const handler = _useGlobalSeacrh();
    if(!handler) {
        throw new Error('Store is not provided');
    }
    return handler;
}
